import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';

import { IdentityModel } from '@core/models/identity.model';
import { UserModel } from '@core/models/user.model';
import { getUserId } from '@core/utils/getUserId';
import { ImagePreviewPipe } from '@ux/pipes/image-preview.pipe';
import moment from 'moment';

type AvatarUser = UserModel | IdentityModel;

@Component({
  selector: 'ux-avatar',
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.less'],
  imports: [DatePipe, CommonModule],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarComponent {
  private readonly imagePreviewPipe = inject(ImagePreviewPipe);
  private readonly authUserId = getUserId();

  private readonly user$ = signal<AvatarUser | null>(null);
  private readonly active$ = signal<boolean>(false);

  @Input() showDeleteBtn = false;
  @Output() remove = new EventEmitter<UserModel>();

  protected readonly image = computed(() => {
    const user = this.user$();
    if (!user?.avatar) {
      return null;
    }
    return this.imagePreviewPipe.transform(user.avatar, 150, 150, true);
  });

  protected readonly shortUserName = computed(() => {
    const user = this.user$();
    if (!user?.firstName) {
      return null;
    }
    return user.firstName[0] || '?';
  });

  protected readonly isOnline = computed(() => {
    const user = this.user$();
    if (!user) {
      return false;
    }
    if (user.id === this.authUserId) {
      return true;
    }

    const lastActiveDate = moment(user.activityAt).add(5, 'minutes').valueOf();
    return lastActiveDate >= moment().valueOf();
  });

  @HostBinding('class.avatar_active')
  get isActiveClass(): boolean {
    return this.active$();
  }

  @HostBinding('class.avatar_bg-empty')
  get isEmptyClass(): boolean {
    const user = this.user$();
    return !user || !('avatarCssStyle' in user);
  }

  @HostBinding('class')
  get avatarStyle(): string {
    const user = this.user$();
    if (user && 'avatarCssStyle' in user) {
      return user.avatarCssStyle;
    }
    return '';
  }

  @Input()
  set active(value: boolean) {
    this.active$.set(value);
  }

  @Input()
  set user(user: AvatarUser | null) {
    this.user$.set(user);
  }

  get user(): AvatarUser | null {
    return this.user$();
  }

  protected onRemoveClick(): void {
    const currentUser = this.user$();
    if (!currentUser) {
      return;
    }

    // TO DO (bad types)
    this.remove.emit(currentUser as UserModel);
  }
}
